const DEFAULT_AUTH_API_URL = "http://localhost:8080";

// Determina si estamos en desarrollo o producción
const isDevelopment = process.env.NODE_ENV === 'development' ||
  (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'));

// Lee primero de window.env (dinámico, útil para producción sin rebuild)
// Luego de process.env (útil para desarrollo con archivos .env)
// Finalmente usa el default
const getAuthApiUrl = () => {
  // En desarrollo, usa rutas relativas (proxy)
  if (isDevelopment) {
    return ''; // Ruta relativa, el proxy la manejará
  }

  // En producción, usa la URL completa
  const raw =
    (typeof window !== 'undefined' && window.env?.REACT_APP_AUTH_API_URL) ||
    (typeof window !== 'undefined' && window.env?.REACT_APP_API_URL) ||
    process.env.REACT_APP_AUTH_API_URL ||
    process.env.REACT_APP_API_URL ||
    DEFAULT_AUTH_API_URL;
  return raw.endsWith("/") ? raw.slice(0, -1) : raw;
};

const AUTH_API_URL = getAuthApiUrl();

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    'Accept': '*/*',
    'Authorization': `Bearer ${token}`
  };
};

const getAuthHeadersWithContentType = () => {
  const token = localStorage.getItem("token");
  return {
    'Content-Type': 'application/json',
    'Accept': '*/*',
    'Authorization': `Bearer ${token}`
  };
};

const COUNTRY_DISPLAY_TO_BACKEND = {
  'REPUBLICA DE COREA': 'Corea, República de'
};

const COUNTRY_BACKEND_TO_DISPLAY = {
  'Corea, República de': 'REPUBLICA DE COREA'
};

const isCountryField = (key) => {
  const normalizedKey = key.toLowerCase();
  return normalizedKey.includes('pais') ||
    normalizedKey === 'nacionalidad' ||
    normalizedKey === 'residenciafiscal';
};

const normalizeCountryValues = (value, countryMap, key = '') => {
  if (typeof value === 'string') {
    return isCountryField(key) ? countryMap[value] || value : value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => normalizeCountryValues(item, countryMap, key));
  }

  if (value && value.constructor === Object) {
    return Object.fromEntries(
      Object.entries(value).map(([childKey, item]) => [childKey, normalizeCountryValues(item, countryMap, childKey)])
    );
  }

  return value;
};

const stringifyRequestBody = (datos) => JSON.stringify(normalizeCountryValues(datos, COUNTRY_DISPLAY_TO_BACKEND));

const parseJsonResponse = async (response) => normalizeCountryValues(await response.json(), COUNTRY_BACKEND_TO_DISPLAY);

const normalizeFirmanteRequest = (datos) => normalizeCountryValues(datos, COUNTRY_DISPLAY_TO_BACKEND);

export const solicitudDetalleService = {
  // Utilidades geográficas
  getProvincias,
  getLocalidades,

  // Archivos
  getArchivo,
  uploadArchivo,

  // PDF
  getSolicitudPdf,

  // Notificaciones
  enviarMailBienvenida,
  enviarMailDatosAcceso,

  // Acciones de solicitud
  aprobarSolicitud,
  eliminarSolicitud,
  cambiarEstadoIncompleta,

  // Firma Digital
  getEstadoFirmaDigital,
  enviarFirmaDigital,
  cancelarFirmaDigital,
  descargarDocumentoFirmado,

  // Auditoría
  getAuditoriaSolicitud,

  // Productores
  obtenerProductores,
  obtenerProductorPorId,
  crearProductor,
  actualizarProductor,
  eliminarProductor,
  asignarProductor,

  // Individuo - GET
  getDatosPrincipalesIndividuo,
  getDatosPersonalesIndividuo,
  getDomicilioIndividuo,
  getDatosFiscalesIndividuo,
  getDatosFiscalesExteriorIndividuo,
  getCuentasBancariasIndividuo,
  getCuentasBancariasExteriorIndividuo,
  getDeclaracionesIndividuo,
  getDeclaracionIngresosIndividuo,
  getDDJJActividadOrigenFondos,
  getPerfilInversorIndividuo,
  getTerminosCondicionesIndividuo,
  getCoTitularesIndividuo,

  // Individuo - SAVE
  saveDatosPrincipalesIndividuo,
  saveDatosPersonalesIndividuo,
  saveDomicilioIndividuo,
  saveDatosFiscalesIndividuo,
  saveDatosFiscalesExteriorIndividuo,
  saveCuentasBancariasIndividuo,
  saveCuentasBancariasExteriorIndividuo,
  saveDeclaracionesIndividuo,
  saveDeclaracionIngresosIndividuo,
  saveDDJJActividadOrigenFondos,
  savePerfilInversorIndividuo,
  saveTerminosCondicionesIndividuo,
  saveCoTitularesIndividuo,

  // Empresa - GET
  getDatosPrincipalesEmpresa,
  getDatosOrganizacionEmpresa,
  getDomicilioEmpresa,
  getDatosFiscalesEmpresa,
  getDatosFiscalesExteriorEmpresa,
  getDatosRegistroEmpresa,
  getCuentasBancariasEmpresa,
  getCuentasBancariasExteriorEmpresa,
  getDeclaracionesEmpresa,
  getPerfilInversorEmpresa,
  getDocumentacionRespaldatoriaEmpresa,
  getFirmantesEmpresa,
  getAccionistasEmpresa,
  getAccionistaPorDocumento,
  getTerminosCondicionesEmpresa,

  // Empresa - SAVE
  saveDatosPrincipalesEmpresa,
  saveDatosOrganizacionEmpresa,
  saveDomicilioEmpresa,
  saveDatosFiscalesEmpresa,
  saveDatosFiscalesExteriorEmpresa,
  saveDatosRegistroEmpresa,
  saveCuentasBancariasEmpresa,
  saveCuentasBancariasExteriorEmpresa,
  saveDeclaracionesEmpresa,
  savePerfilInversorEmpresa,
  saveDocumentacionRespaldatoriaEmpresa,
  saveFirmantesEmpresa,
  createFirmanteEmpresa,
  updateFirmanteEmpresa,
  deleteFirmanteEmpresa,
  saveAccionistasEmpresa,
  createAccionistaEmpresa,
  updateAccionistaEmpresa,
  deleteAccionistaEmpresa,
  saveTerminosCondicionesEmpresa
};

// ========== INDIVIDUO ==========

async function getDatosPrincipalesIndividuo(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/datos-principales/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getDatosPersonalesIndividuo(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/datos-personales/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getDomicilioIndividuo(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/domicilio/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getDatosFiscalesIndividuo(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/datos-fiscales/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getDatosFiscalesExteriorIndividuo(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/datos-fiscales-exterior/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getCuentasBancariasIndividuo(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/cuentas-bancarias/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getCuentasBancariasExteriorIndividuo(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/cuentas-bancarias-exterior/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getDeclaracionesIndividuo(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/declaraciones/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getDeclaracionIngresosIndividuo(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/ddjj-origen-fondos/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getDDJJActividadOrigenFondos(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/solicitudes/${solicitudId}/ddjj-actividad-origen-fondos`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}


async function getPerfilInversorIndividuo(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/perfil-inversor/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getTerminosCondicionesIndividuo(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/terminos-condiciones/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getCoTitularesIndividuo(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/apertura-posterior/firmantes/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

// ========== EMPRESA ==========

async function getDatosPrincipalesEmpresa(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/datos-principales/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getDatosOrganizacionEmpresa(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/datos-empresa/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getDomicilioEmpresa(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/domicilio/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getDatosFiscalesEmpresa(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/datos-fiscales/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getDatosFiscalesExteriorEmpresa(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/datos-fiscales-exterior/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getDatosRegistroEmpresa(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/datos-registro/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getCuentasBancariasEmpresa(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/cuentas-bancarias/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getCuentasBancariasExteriorEmpresa(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/cuentas-bancarias-exterior/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getDeclaracionesEmpresa(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/declaraciones/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getPerfilInversorEmpresa(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/perfil-inversor/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getDocumentacionRespaldatoriaEmpresa(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/documentacion-respaldatoria/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getFirmantesEmpresa(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/apertura-posterior/firmantes/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getAccionistasEmpresa(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/accionistas/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getTerminosCondicionesEmpresa(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/terminos-condiciones/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

// ========== INDIVIDUO - SAVE METHODS ==========

async function saveDatosPrincipalesIndividuo(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/datos-principales/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveDatosPersonalesIndividuo(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/datos-personales/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveDomicilioIndividuo(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/domicilio/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveDatosFiscalesIndividuo(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/datos-fiscales/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveDatosFiscalesExteriorIndividuo(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/datos-fiscales-exterior/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveCuentasBancariasIndividuo(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/cuentas-bancarias/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveCuentasBancariasExteriorIndividuo(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/cuentas-bancarias-exterior/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveDeclaracionesIndividuo(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/declaraciones/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveDeclaracionIngresosIndividuo(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/ddjj-origen-fondos/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveDDJJActividadOrigenFondos(datos) {
  // First, try to GET to see if the record exists
  try {
    const checkResponse = await fetch(`${AUTH_API_URL}/api/solicitudes/${datos.solicitudId}/ddjj-actividad-origen-fondos`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    // If record exists (200), use PUT to update
    if (checkResponse.ok) {
      const response = await fetch(`${AUTH_API_URL}/api/solicitudes/${datos.solicitudId}/ddjj-actividad-origen-fondos`, {
        method: 'PUT',
        headers: getAuthHeadersWithContentType(),
        body: stringifyRequestBody(datos)
      });
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      try {
        return await parseJsonResponse(response);
      } catch {
        return { ok: true };
      }
    } else {
      // If record doesn't exist (404), use POST to create
      const response = await fetch(`${AUTH_API_URL}/api/solicitudes/${datos.solicitudId}/ddjj-actividad-origen-fondos`, {
        method: 'POST',
        headers: getAuthHeadersWithContentType(),
        body: stringifyRequestBody(datos)
      });
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      try {
        return await parseJsonResponse(response);
      } catch {
        return { ok: true };
      }
    }
  } catch (err) {
    throw err;
  }
}


async function savePerfilInversorIndividuo(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/perfil-inversor/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveTerminosCondicionesIndividuo(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/terminos-condiciones/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveCoTitularesIndividuo(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/individuo-apertura/cotitulares/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

// ========== EMPRESA - SAVE METHODS ==========

async function saveDatosPrincipalesEmpresa(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/datos-principales/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveDatosOrganizacionEmpresa(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/datos-empresa/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveDomicilioEmpresa(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/domicilio/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveDatosFiscalesEmpresa(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/datos-fiscales/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveDatosFiscalesExteriorEmpresa(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/datos-fiscales-exterior/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveDatosRegistroEmpresa(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/datos-registro/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveCuentasBancariasEmpresa(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/cuentas-bancarias/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveCuentasBancariasExteriorEmpresa(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/cuentas-bancarias-exterior/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveDeclaracionesEmpresa(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/declaraciones/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function savePerfilInversorEmpresa(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/perfil-inversor/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveDocumentacionRespaldatoriaEmpresa(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/documentacion-respaldatoria/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveFirmantesEmpresa(datos) {
  const firmantesNormalizados = normalizeFirmanteRequest(datos);
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/firmantes/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: JSON.stringify(firmantesNormalizados)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function createFirmanteEmpresa(solicitudId, datos) {
  const firmanteNormalizado = normalizeFirmanteRequest(datos);
  const response = await fetch(`${AUTH_API_URL}/api/apertura-posterior/firmantes/${solicitudId}`, {
    method: 'POST',
    headers: getAuthHeadersWithContentType(),
    body: JSON.stringify(firmanteNormalizado)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function updateFirmanteEmpresa(solicitudId, firmanteId, datos) {
  const firmanteNormalizado = normalizeFirmanteRequest(datos);
  const response = await fetch(`${AUTH_API_URL}/api/apertura-posterior/firmantes/${solicitudId}/${firmanteId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: JSON.stringify(firmanteNormalizado)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function deleteFirmanteEmpresa(solicitudId, firmanteId) {
  const response = await fetch(`${AUTH_API_URL}/api/apertura-posterior/firmantes/${solicitudId}/${firmanteId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  // DELETE puede no devolver cuerpo
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveAccionistasEmpresa(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/accionistas/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function createAccionistaEmpresa(solicitudId, datos) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/accionistas/${solicitudId}`, {
    method: 'POST',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function updateAccionistaEmpresa(solicitudId, accionistaId, datos) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/accionistas/${solicitudId}/${accionistaId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function deleteAccionistaEmpresa(solicitudId, accionistaId) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/accionistas/${solicitudId}/${accionistaId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function saveTerminosCondicionesEmpresa(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/terminos-condiciones/${datos.solicitudId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

// ========== UTILIDADES GEOGRÁFICAS ==========

async function getProvincias() {
  const response = await fetch(`${AUTH_API_URL}/api/ambito/provincias`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getLocalidades(provinciaId) {
  const response = await fetch(`${AUTH_API_URL}/api/ambito/localidades/${provinciaId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function getAccionistaPorDocumento(solicitudId, numeroDocumento) {
  const response = await fetch(`${AUTH_API_URL}/api/empresa-apertura/accionistas/${solicitudId}/documento/${numeroDocumento}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) {
    // Si no encuentra el accionista, devolver null en lugar de lanzar error
    if (response.status === 404) return null;
    throw new Error(`Error: ${response.status}`);
  }
  return await parseJsonResponse(response);
}

// ========== ARCHIVOS ==========

async function getArchivo(archivoId) {
  const response = await fetch(`${AUTH_API_URL}/api/archivos/${archivoId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) {
    if (response.status === 404) return null;
    throw new Error(`Error: ${response.status}`);
  }
  return await parseJsonResponse(response);
}

async function uploadArchivo(formData) {
  const token = localStorage.getItem("token");
  const response = await fetch(`${AUTH_API_URL}/api/archivos/upload`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: formData
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

// ========== PDF ==========

async function getSolicitudPdf(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/solicitudes/${solicitudId}/pdf`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  // Devolver el blob para descargar
  return await response.blob();
}

// ========== NOTIFICACIONES ==========

async function enviarMailBienvenida(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/solicitudes/${solicitudId}/enviar-bienvenida`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  // Puede no devolver cuerpo
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function enviarMailDatosAcceso(solicitudId, usuario, clave, correosElectronicos) {
  const params = new URLSearchParams({
    usuario,
    clave,
    correosElectronicos
  });
  const response = await fetch(`${AUTH_API_URL}/api/solicitudes/${solicitudId}/enviar-datos-acceso?${params.toString()}`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  // Puede no devolver cuerpo
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

// ========== ACCIONES DE SOLICITUD ==========

async function aprobarSolicitud(solicitudId, motivo, idCuenta) {
  const params = new URLSearchParams();
  if (motivo) params.append('motivo', motivo);
  if (idCuenta) params.append('idCuenta', idCuenta);

  const response = await fetch(`${AUTH_API_URL}/api/solicitudes/${solicitudId}/aprobar?${params.toString()}`, {
    method: 'PUT',
    headers: getAuthHeaders()
  });
  if (!response.ok) {
    // Intentar obtener el mensaje de error del backend
    let errorMessage = `Error: ${response.status}`;
    try {
      const errorData = await parseJsonResponse(response);
      if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch {
      // Si no se puede parsear el JSON, usar el mensaje por defecto
    }
    throw new Error(errorMessage);
  }
  // Puede no devolver cuerpo
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function eliminarSolicitud(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/solicitudes/${solicitudId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  // DELETE puede no devolver cuerpo
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function cambiarEstadoIncompleta(solicitudId) {
  const params = new URLSearchParams({
    estado: 'INCOMPLETA'
  });
  const response = await fetch(`${AUTH_API_URL}/api/solicitudes/${solicitudId}/estado?${params.toString()}`, {
    method: 'PUT',
    headers: getAuthHeaders()
  });
  if (!response.ok) {
    // Intentar obtener el mensaje de error del backend
    let errorMessage = `Error: ${response.status}`;
    try {
      const errorData = await parseJsonResponse(response);
      if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch {
      // Si no se puede parsear el JSON, usar el mensaje por defecto
    }
    throw new Error(errorMessage);
  }
  // Puede no devolver cuerpo
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

// ========== FIRMA DIGITAL ==========

async function getEstadoFirmaDigital(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/firma-digital/${solicitudId}/estado`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (response.status === 404) {
    return { notFound: true };
  }
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function enviarFirmaDigital(solicitudId, emails) {
  // emails es un array de strings
  const params = new URLSearchParams();
  emails.forEach(email => params.append('emails', email));

  const response = await fetch(`${AUTH_API_URL}/api/firma-digital/${solicitudId}/enviar?${params.toString()}`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function cancelarFirmaDigital(solicitudId, razon) {
  const params = new URLSearchParams();
  params.append('razon', razon);

  const response = await fetch(`${AUTH_API_URL}/api/firma-digital/${solicitudId}/cancelar?${params.toString()}`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  if (!response.ok) {
    const errorData = await parseJsonResponse(response).catch(() => ({ message: `Error: ${response.status}` }));
    throw new Error(errorData.message || errorData.error || `Error: ${response.status}`);
  }
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function descargarDocumentoFirmado(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/firma-digital/${solicitudId}/pdf-firmado`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) {
    let errorMessage = `Error: ${response.status}`;
    try {
      const errorData = await parseJsonResponse(response);
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch { }
    throw new Error(errorMessage);
  }
  return await response.blob();
}

// ========== AUDITORÍA ==========

async function getAuditoriaSolicitud(solicitudId) {
  const response = await fetch(`${AUTH_API_URL}/api/auditoria/solicitud/${solicitudId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

// ========== PRODUCTORES ==========

async function obtenerProductores() {
  const response = await fetch(`${AUTH_API_URL}/api/productores`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function obtenerProductorPorId(id) {
  const response = await fetch(`${AUTH_API_URL}/api/productores/${id}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return await parseJsonResponse(response);
}

async function crearProductor(datos) {
  const response = await fetch(`${AUTH_API_URL}/api/productores`, {
    method: 'POST',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function actualizarProductor(id, datos) {
  const response = await fetch(`${AUTH_API_URL}/api/productores/${id}`, {
    method: 'PUT',
    headers: getAuthHeadersWithContentType(),
    body: stringifyRequestBody(datos)
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}

async function eliminarProductor(id) {
  const response = await fetch(`${AUTH_API_URL}/api/productores/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  if (response.status === 400) {
    throw new Error('No se pudo eliminar el productor');
  }
  if (!response.ok && response.status !== 204) {
    throw new Error(`Error: ${response.status}`);
  }
  return { ok: true };
}

async function asignarProductor(idSolicitud, idProductor) {
  const params = new URLSearchParams({
    idSolicitud: idSolicitud.toString(),
    idProductor: idProductor.toString()
  });
  const response = await fetch(`${AUTH_API_URL}/api/productores/asignar?${params.toString()}`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  try {
    return await parseJsonResponse(response);
  } catch {
    return { ok: true };
  }
}
